import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@resource-kit/ui/react';
import { STAGE_LABEL } from '../board/BatchBoard';
import {
  useExceptionOrCancelled,
  type ExceptionOrCancelledWire,
} from '../hooks/useExceptionOrCancelled';

type Item = ExceptionOrCancelledWire['items'][number];

const REASON_KEY = {
  preflight_order_cancelled: 'dispatch.pane.exception.reason.preflight_order_cancelled',
  operator_before_dispatch: 'dispatch.pane.exception.reason.operator_before_dispatch',
  operator_after_dispatch: 'dispatch.pane.exception.reason.operator_after_dispatch',
  shopify_order_cancelled: 'dispatch.pane.exception.reason.shopify_order_cancelled',
  shopify_date_removed: 'dispatch.pane.exception.reason.shopify_date_removed',
  delivery_failed: 'dispatch.pane.exception.reason.delivery_failed',
  unknown: 'dispatch.pane.exception.reason.unknown',
} as const satisfies Record<Item['reason'], string>;

const STATUS_KEY = {
  exception: 'job.enum.exception',
  cancelled: 'job.enum.cancelled',
} as const satisfies Record<Item['status'], string>;

const BADGE_CLASS = {
  exception: 'border-transparent bg-destructive/15 text-destructive',
  cancelled: 'border-transparent bg-muted text-muted-foreground',
} as const satisfies Record<Item['status'], string>;

interface ExceptionPaneProps {
  date: string;
}

/**
 * The exception / cancelled pane (#2481): the day's exception and cancelled
 * delivery jobs in the order the backend gives (exceptions first, then by
 * name), each with its reason, plus a fixed note on what happens when a
 * delivery fails. The note shows in every state, including zero rows — the
 * detail pane never renders its empty state for this column.
 */
export function ExceptionPane({ date }: ExceptionPaneProps) {
  const { t } = useTranslation();
  const query = useExceptionOrCancelled(date);
  const items = query.data?.items ?? [];

  const list = () => {
    if (query.isError) {
      return (
        <div className="flex items-center gap-3 px-4 py-3 text-sm">
          <span className="text-destructive">{t('dispatch.pane.exception.loadFailed')}</span>
          <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
            {t('ui.retry')}
          </Button>
        </div>
      );
    }
    if (query.isLoading) {
      return (
        <div className="h-12 animate-pulse bg-muted/40" data-testid="dispatch-exception-loading" />
      );
    }
    return items.map((item) => (
      <div
        key={item.job_id}
        data-testid={`dispatch-exception-row-${item.job_id}`}
        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3.5 gap-y-1 border-t border-border/60 px-4 py-2.5 first:border-t-0 lg:grid-cols-[minmax(150px,auto)_minmax(0,1fr)_auto]"
      >
        <span className="font-mono text-[12.5px]">{item.job_name ?? '—'}</span>
        <span className="order-3 col-span-full text-[13px] text-muted-foreground lg:order-none lg:col-span-1">
          {`${t(STATUS_KEY[item.status])} · ${t(REASON_KEY[item.reason])}`}
        </span>
        <Badge
          variant="outline"
          data-testid="dispatch-exception-badge"
          className={BADGE_CLASS[item.status]}
        >
          {t(STATUS_KEY[item.status])}
        </Badge>
      </div>
    ));
  };

  const hasRows = query.isError || query.isLoading || items.length > 0;
  return (
    <div
      data-testid="dispatch-pane-exception_or_cancelled"
      className="overflow-hidden rounded-xl border border-border bg-card"
    >
      {list()}
      <p
        data-testid="dispatch-exception-note"
        className={`px-4 py-5 text-[13px] text-muted-foreground ${
          hasRows ? 'border-t border-border/60' : ''
        }`}
      >
        <b className="text-foreground">{t('dispatch.pane.exception.failedTitle')}</b>
        {t('dispatch.pane.exception.failedBody', {
          pending: t(STAGE_LABEL.awaiting_dispatch),
        })}
      </p>
    </div>
  );
}
